import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Star, Save, Send, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="text-white/45 text-xs uppercase tracking-widest">{label}</div>
      <div className="font-display font-black text-2xl mt-1.5 text-white">{value ?? "—"}</div>
      {sub && <div className="text-white/40 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}

const Stars = ({ n }) => (
  <span className="text-[#F2B705]">{"★".repeat(Math.round(n || 0))}<span className="text-white/20">{"★".repeat(5 - Math.round(n || 0))}</span></span>
);

export default function ReputationDashboard({ token }) {
  const [d, setD] = useState(null);
  const [ext, setExt] = useState(null);
  const [saving, setSaving] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => axios.get(`${API}/marketing/reputation`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => {
    setD(r.data);
    setExt({ google_count: r.data.google_count || "", google_rating: r.data.google_rating || "",
             facebook_count: r.data.facebook_count || "", facebook_rating: r.data.facebook_rating || "" });
  }), [token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/marketing/reputation`, {
        google_count: Number(ext.google_count) || 0, google_rating: Number(ext.google_rating) || 0,
        facebook_count: Number(ext.facebook_count) || 0, facebook_rating: Number(ext.facebook_rating) || 0,
      }, H);
      toast.success("External review counts saved.");
      load();
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  if (!d || !ext) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading reputation…</div>;

  return (
    <div data-testid="reputation-dashboard" className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="rep-overall" label="Average Rating" value={d.overall_avg ? `${d.overall_avg} ★` : "—"} sub="weighted across platforms" />
        <Stat testid="rep-google" label="Google Reviews" value={d.google_count} sub={d.google_rating ? `${d.google_rating} ★` : "set rating below"} />
        <Stat testid="rep-facebook" label="Facebook Reviews" value={d.facebook_count} sub={d.facebook_rating ? `${d.facebook_rating} ★` : "set rating below"} />
        <Stat testid="rep-website" label="Website Reviews" value={d.website_count} sub={d.website_avg ? `${d.website_avg} ★ · ${d.website_approved} approved` : "none yet"} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat testid="rep-requests" label="Review Requests Sent" value={d.requests_sent} />
        <Stat testid="rep-response-rate" label="Review Response Rate" value={d.response_rate != null ? `${d.response_rate}%` : "—"} sub="reviews received ÷ requests sent" />
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
        <h3 className="font-display font-bold text-white mb-1">Google & Facebook Counts (manual for now)</h3>
        <p className="text-white/45 text-sm mb-4">Update these from your Google Business Profile / Facebook page. Live syncing comes when we connect the Google Business Profile API.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[["google_count", "Google review count"], ["google_rating", "Google rating (1-5)"], ["facebook_count", "Facebook review count"], ["facebook_rating", "Facebook rating (1-5)"]].map(([k, l]) => (
            <label key={k} className="text-xs text-white/50 uppercase tracking-wider">{l}
              <input data-testid={`rep-input-${k}`} type="number" step="0.1" value={ext[k]} onChange={(e) => setExt({ ...ext, [k]: e.target.value })}
                className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
            </label>
          ))}
        </div>
        <button data-testid="rep-save-btn" onClick={save} disabled={saving} className="mt-4 flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
        <h3 className="font-display font-bold text-white mb-3 flex items-center gap-2"><MessageCircle size={16} className="text-[#F2B705]" /> Latest Website Reviews</h3>
        <div className="space-y-2.5">
          {d.recent.map((r, i) => (
            <div key={i} className="bg-black/30 border border-white/5 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 text-sm"><span className="text-white font-semibold">{r.name}</span> <Stars n={r.rating} /> <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${r.status === "approved" ? "bg-green-500/15 text-green-400" : "bg-white/5 text-white/50"}`}>{r.status}</span>
                <span className="ml-auto text-white/35 text-xs">{(r.created_at || "").slice(0, 10)}</span></div>
              {r.comment && <div className="text-white/60 text-xs mt-1">{r.comment}</div>}
            </div>
          ))}
          {d.recent.length === 0 && <div className="text-white/40 text-sm py-4 text-center flex items-center justify-center gap-2"><Send size={14} /> No website reviews yet — review requests go out automatically after each return.</div>}
        </div>
      </div>
    </div>
  );
}
